import { ArrowRight, ContactBand, Feature, SectionHeading, TrustedSlider } from '../../components/Layout';
import { REASONS, SERVICES_HOME, STATS } from '../../data/siteData';
import { useRef } from 'react';

function HeroTeamPhoto() {
  return (
    <div className="hero-team-card">
      <div className="hero-photo-glow" aria-hidden="true" />
      <img
        className="hero-team-img"
        src="../../BG.webp"

        alt="ClearDesk support agents wearing headsets"
        onError={(event) => {
          event.currentTarget.style.display = 'none';
          event.currentTarget.nextElementSibling.style.display = 'grid';
        }}
      />
      <div className="hero-photo-fallback" aria-hidden="true" style={{ display: 'none' }}>
        <span>CD</span>
        <strong>Support Team</strong>
      </div>
      <div className="hero-photo-card hero-photo-card-top">
        <span className="mini-status" />
        <div>
          <strong>Live coverage</strong>
          <span>Calls, bookings, inboxes</span>
        </div>
      </div>
      <div className="hero-photo-card hero-photo-card-bottom">
        <strong>24/7</strong>
        <span>Support options</span>
      </div>
    </div>
  );
}

function ServiceCard({ service }) {
  const cardRef = useRef(null);

  const handleMouseMove = (e) => {
    const card = cardRef.current;

    const rect = card.getBoundingClientRect();

    const x = e.clientX - rect.left;
    const y = e.clientY - rect.top;

    const centerX = rect.width / 2;
    const centerY = rect.height / 2;

    const rotateX = ((y - centerY) / centerY) * -10;
    const rotateY = ((x - centerX) / centerX) * 10;

    card.style.transform = `
      perspective(1000px)
      rotateX(${rotateX}deg)
      rotateY(${rotateY}deg)
      translateY(-8px)
      scale(1.02)
    `;
  };

  const handleMouseLeave = () => {
    const card = cardRef.current;

    card.style.transform = `
      perspective(1000px)
      rotateX(0deg)
      rotateY(0deg)
      translateY(0px)
      scale(1)
    `;
  };

  return (
    <article
      ref={cardRef}
      className="service-card reveal reveal-d2 in-view"
      onMouseMove={handleMouseMove}
      onMouseLeave={handleMouseLeave}
    >
      <div className="card-illus-wrap">
        <span className="card-icon">{service.abbr}</span>
      </div>

      <h3>{service.title}</h3>
      <p>{service.text}</p>
    </article>
  );
}

function Step({ number, title, text }) {
  return (
    <article className="step-card">
      <span className="step-number">{number}</span>
      <h3>{title}</h3>
      <p>{text}</p>
    </article>
  );
}

export default function HomePage({ onNavigate }) {
  return (
    <>
      <section className="hero" aria-label="Hero section">
        <div className="hero-bg" aria-hidden="true">
          <div className="hero-bg-grid" />
          <div className="hero-glow-1" />
          <div className="hero-glow-2" />
          <div className="hero-noise" />
        </div>
        <div className="hero-inner">
          <div className="hero-copy">
            <div className="live-badge">
              <span className="live-dot" />
              Outsourced customer support for UK businesses
            </div>
            <h1>Your business,<br /><em>always covered.</em></h1>
            <p className="hero-text">
              Professional call handling, booking support, WhatsApp, email, and overflow services delivered with the
              clarity and care your customers expect.
            </p>
            <div className="hero-actions">
              <button className="btn primary" onClick={() => onNavigate('/contact')}>
                Get a free consultation <ArrowRight />
              </button>
              <button className="btn ghost-dark" onClick={() => onNavigate('/services')}>View all services</button>
            </div>
            <div className="hero-stats">
              {STATS.map((stat) => (
                <div className="metric" key={stat.label}>
                  <strong>{stat.value}</strong>
                  <span>{stat.label}</span>
                </div>
              ))}
            </div>
          </div>
          <HeroTeamPhoto />
        </div>
      </section>

      <TrustedSlider />

      <section className="services-band full-band">
        <div className="section-pad">
          <SectionHeading
            eyebrow="What we do"
            title="Complete customer support solutions"
            text="From first contact to resolution, ClearDesk handles customer touchpoints with structure, professionalism, and care."
          />
          <div className="service-grid">
            {SERVICES_HOME.map((service) => <ServiceCard key={service.title} service={service} />)}
          </div>
        </div>
      </section>

      <section className="why-section full-band section-pad">
        <SectionHeading
          eyebrow="Why choose us"
          title="A support partner built for trust and scale"
          text="Reliable, measurable, and easy to start for growing service businesses."
          light
        />
        <div className="reason-grid">
          {REASONS.map((reason) => <Feature key={reason.title} title={reason.title} text={reason.text} />)}
        </div>
      </section>

      <section className="section-pad">
        <SectionHeading
          eyebrow="How it works"
          title="Three steps to a clearer support desk"
          text="A simple onboarding path makes the service easy for business owners to understand and buy."
        />
        <div className="steps">
          <Step number="01" title="Discovery" text="We learn your business, customers, opening hours, booking process, and escalation rules." />
          <Step number="02" title="Setup" text="We prepare scripts, channel rules, message templates, reporting preferences, and quality checks." />
          <Step number="03" title="Go live" text="ClearDesk begins handling enquiries while you receive clean updates and practical reporting." />
        </div>
      </section>






      <section class="why-section full-band section-pad" style={{ "padding-top": "40px", "padding-bottom": "80px" }}>
        <div class="section-heading reveal light in-view">
          <p class="eyebrow" style={{ "color": "var(--cyan)" }}>Client results</p>
          <h2>What our clients say</h2>
        </div>
        <div class="testimonials-grid">
          <div class="testimonial-card reveal reveal-d1 in-view">
            <div class="testimonial-avatar" style={{ "--avatar-color": "#1a56ff" }}>
              <span>SM</span>
              <div class="avatar-ring"></div>
            </div>
            <div class="testimonial-stars">★★★★★</div>
            <p class="testimonial-text">"ClearDesk transformed our patient enquiries. Staff now focus entirely on in-clinic care while ClearDesk handles all inbound calls professionally."</p>
            <div class="testimonial-author">Dr. Sarah Mitchell</div>
            <div class="testimonial-role">GP Practice Manager, London</div>
          </div>
          <div class="testimonial-card reveal reveal-d2 in-view">
            <div class="testimonial-avatar" style={{ "--avatar-color": "#00c8f0" }}>
              <span>JT</span>
              <div class="avatar-ring"></div>
            </div>
            <div class="testimonial-stars">★★★★★</div>
            <p class="testimonial-text">"After-hours support was a game changer. We were losing leads every evening. Now every enquiry is captured — conversions improved significantly."</p>
            <div class="testimonial-author">James Thorne</div>
            <div class="testimonial-role">Director, Thorne Estate Agents</div>
          </div>
          <div class="testimonial-card reveal reveal-d3 in-view">
            <div class="testimonial-avatar" style={{ "--avatar-color": "#22d97b" }}>
              <span>RO</span>
              <div class="avatar-ring"></div>
            </div>
            <div class="testimonial-stars">★★★★★</div>
            <p class="testimonial-text">"Overflow during our busy season was seamless. Customers didn't know they were with an outsourced team. That's how good the training is."</p>
            <div class="testimonial-author">Rachel Okafor</div>
            <div class="testimonial-role">Operations Manager, Okafor Legal</div>
          </div>
        </div>
      </section>


<section className="metrics-band full-band section-pad">
  <div className="metrics-inner">

    {/* TEXT SECTION */}
    <div className="metrics-text reveal in-view">
      <p className="eyebrow">Performance that matters</p>

      <h2>Built around outcomes, not just activity.</h2>

      <p>
        We measure what matters to your business — not just calls answered,
        but leads captured, bookings confirmed, and customers who feel heard.
      </p>

      <button
        className="btn primary"
        onClick={() => onNavigate('/about')}
        style={{ marginTop: "24px" }}
      >
        See how it works

        <svg
          className="btn-arrow"
          width="14"
          height="14"
          viewBox="0 0 14 14"
          fill="none"
        >
          <path
            d="M2 7h10M8 3l4 4-4 4"
            stroke="currentColor"
            strokeWidth="1.8"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
        </svg>
      </button>
    </div>

    {/* VISUAL SECTION */}
    {/* Replace the entire metrics-visual div with this */}
<div className="metrics-visual reveal reveal-d2">
  <div className="metrics-ring-wrap">

    {/* SVG RINGS */}
    <svg viewBox="0 0 240 240" width="240" height="240" className="metrics-svg">
      {/* Outer ring — Call resolution 94% */}
      <circle cx="120" cy="120" r="108" stroke="rgba(255,255,255,0.05)" strokeWidth="12" fill="none"/>
      <circle cx="120" cy="120" r="108" stroke="#1a56ff" strokeWidth="12" fill="none"
        strokeLinecap="round" className="ring-progress"
        style={{"--fill":"638px","--circ":"678px","--offset":"169px","--delay":"0s"}}/>

      {/* Second ring — Satisfaction 98% */}
      <circle cx="120" cy="120" r="84" stroke="rgba(255,255,255,0.05)" strokeWidth="10" fill="none"/>
      <circle cx="120" cy="120" r="84" stroke="#00c8f0" strokeWidth="10" fill="none"
        strokeLinecap="round" className="ring-progress"
        style={{"--fill":"515px","--circ":"528px","--offset":"132px","--delay":"0.2s"}}/>

      {/* Third ring — SLA 99% */}
      <circle cx="120" cy="120" r="60" stroke="rgba(255,255,255,0.05)" strokeWidth="10" fill="none"/>
      <circle cx="120" cy="120" r="60" stroke="#22d97b" strokeWidth="10" fill="none"
        strokeLinecap="round" className="ring-progress"
        style={{"--fill":"372px","--circ":"377px","--offset":"94px","--delay":"0.4s"}}/>

      {/* Inner ring — Answer time */}
      <circle cx="120" cy="120" r="36" stroke="rgba(255,255,255,0.05)" strokeWidth="8" fill="none"/>
      <circle cx="120" cy="120" r="36" stroke="#f5a623" strokeWidth="8" fill="none"
        strokeLinecap="round" className="ring-progress"
        style={{"--fill":"222px","--circ":"226px","--offset":"56px","--delay":"0.6s"}}/>
    </svg>

    {/* Centre icon */}
    {/* <div className="ring-center-badge">
      <svg width="20" height="20" viewBox="0 0 18 18" fill="none">
        <path d="M4 10 Q4 5 9 5 Q14 5 14 10" stroke="#4a8fff" strokeWidth="2" strokeLinecap="round"/>
        <rect x="2.5" y="9.5" width="3" height="5" rx="1.5" fill="#1a56ff"/>
        <rect x="12.5" y="9.5" width="3" height="5" rx="1.5" fill="#1a56ff"/>
        <path d="M3.5 14.5 Q2 17 4 18" stroke="#1a56ff" strokeWidth="1.5" strokeLinecap="round"/>
        <circle cx="4" cy="18" r="1.5" fill="#00c8f0"/>
      </svg>
      <span>LIVE</span>
    </div> */}

    {/* Stat cards */}
    <div className="metrics-stat-cards">
      {[
        { color:"#1a56ff", label:"Call resolution",      value:"94%", sub:"First-call resolution rate" },
        { color:"#00c8f0", label:"Customer satisfaction", value:"98%", sub:"Post-call survey score"     },
        { color:"#22d97b", label:"Response SLA met",      value:"99%", sub:"Within agreed window"       },
        { color:"#f5a623", label:"Avg answer time",       value:"<30s",sub:"Across all channels"        },
      ].map((s, i) => (
        <div key={s.label} className="metrics-stat-card"
          style={{"--float-delay":`${i+1 * 0.95}s`,"--count-delay":`${i+1 * 1}s`}}>
          <div className="metrics-stat-top">
            <span className="metrics-stat-dot" style={{background: s.color}}/>
            <span className="metrics-stat-label">{s.label}</span>
          </div>
          <div className="metrics-stat-value">{s.value}</div>
          <div className="metrics-stat-sub">{s.sub}</div>
          <div className="metrics-stat-bar">
            <div className="metrics-stat-bar-fill"
              style={{background: s.color, "--bar-pct": s.value.replace('%','') + '%'}}/>
          </div>
        </div>
      ))}
    </div>

  </div>
</div>
  </div>
</section>


      <ContactBand onNavigate={onNavigate} />
    </>
  );
}
